import { defineComponent } from 'vue'
import type { RouteRecordRaw } from 'vue-router'
import { authenticatedChildren } from './authenticated-routes'
import { isPublicRegisterEmbedQuery } from '@/features/recruitment/utils/public-register-embed'

function recruitmentPortalChildren(mode: 'guest' | 'staff'): RouteRecordRaw[] {
  const guest = mode === 'guest'
  const routeMeta = guest
    ? { publicRegister: true, guest: true }
    : { staffRegister: true, requiresAuth: true }
  const ordersName = guest ? 'recruitment-portal-guest-orders' : 'recruitment-portal-staff-orders'
  const intakeName = guest ? 'recruitment-portal-guest-intake' : 'recruitment-portal-staff-intake'
  const submissionsName = guest
    ? 'recruitment-portal-guest-submissions'
    : 'recruitment-portal-staff-submissions'

  const children: RouteRecordRaw[] = [
    {
      path: '',
      redirect: (to) => ({
        name:
          guest && isPublicRegisterEmbedQuery(to.query)
            ? intakeName
            : ordersName,
        params: { ...to.params },
        query: to.query,
      }),
    },
    {
      path: 'orders',
      name: ordersName,
      component: () =>
        import('@/features/recruitment/pages/recruitment-portal/RecruitmentPortalOrdersPage.vue'),
      meta: {
        ...routeMeta,
        pageTitleKey: 'recruitmentPortal.orders.title',
      },
    },
    {
      path: 'intake',
      name: intakeName,
      component: () =>
        import('@/features/recruitment/pages/recruitment-portal/RecruitmentPortalIntakePage.vue'),
      meta: {
        ...routeMeta,
        fullBleed: true,
        pageTitleKey: 'recruitmentPortal.nav.intake',
      },
    },
    {
      path: 'submissions',
      name: submissionsName,
      component: () =>
        import('@/features/recruitment/pages/recruitment-portal/RecruitmentPortalSubmissionsPage.vue'),
      meta: {
        ...routeMeta,
        pageTitleKey: 'recruitmentPortal.submissions.title',
      },
    },
  ]

  if (!guest) {
    children.push({
      path: 'settings',
      name: 'recruitment-portal-staff-settings',
      component: () =>
        import('@/features/recruitment/pages/recruitment-portal/RecruitmentPortalSettingsPage.vue'),
      meta: {
        ...routeMeta,
        pageTitleKey: 'recruitmentPortal.settings.title',
      },
    })
  }

  return children
}

/** Placeholder only — `guards.ts` redirects to the first allowed menu after navigation loads. */
const HomeLanding = defineComponent({
  name: 'HomeLanding',
  setup() {
    return () => null
  },
})

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/pages/LoginPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/recruitment-portal/login',
    name: 'recruitment-portal-login',
    component: () => import('@/features/auth/pages/LoginPage.vue'),
    meta: { guest: true, portalLogin: true },
  },
  {
    // Path form keeps the access token when Zalo strips ?query= on open.
    // Explicit one-segment match — avoid empty capture if trailing slash is present.
    path: '/recruitment-portal/login/a/:access([^/]+)',
    name: 'recruitment-portal-login-access',
    component: () => import('@/features/auth/pages/LoginPage.vue'),
    meta: { guest: true, portalLogin: true },
  },
  {
    path: '/r/:token',
    component: () => import('@/features/recruitment/pages/recruitment-portal/RecruitmentPortalShell.vue'),
    meta: { publicRegister: true, guest: true },
    children: recruitmentPortalChildren('guest'),
  },
  {
    path: '/recruitment-portal',
    component: () => import('@/features/recruitment/pages/recruitment-portal/RecruitmentPortalShell.vue'),
    meta: { staffRegister: true, requiresAuth: true },
    children: recruitmentPortalChildren('staff'),
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/features/auth/pages/ForgotPasswordPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/features/auth/pages/ResetPasswordPage.vue'),
    meta: { guest: true, passwordFlow: 'reset' },
  },
  {
    path: '/setup-password',
    name: 'setup-password',
    component: () => import('@/features/auth/pages/ResetPasswordPage.vue'),
    meta: { guest: true, passwordFlow: 'setup' },
  },
  {
    path: '/',
    component: () => import('@/shared/layout/AppShell.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'home', component: HomeLanding },
      ...authenticatedChildren,
      {
        path: 'profile',
        component: () => import('@/features/profile/pages/ProfileLayoutPage.vue'),
        meta: { fullBleed: true, pageKey: 'profile' },
        children: [
          {
            path: '',
            name: 'profile',
            component: () => import('@/features/profile/pages/ProfilePage.vue'),
          },
          {
            path: 'password',
            name: 'profile-password',
            component: () => import('@/features/profile/pages/ProfilePasswordPage.vue'),
          },
          {
            path: 'two-factor',
            redirect: { name: 'profile' },
          },
        ],
      },
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/shared/pages/ForbiddenPage.vue'),
        meta: { fullBleed: true },
      },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]
